import React, { useEffect, useRef, useState } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';

import { Popover, Tooltip } from 'antd';
import LinkButton from 'components/common/LinkButton/LinkButton';
import NumericInputField from 'components/common/NumericInputField';
import {
  PRODUCTION_MATRIX_REQUIRED_PLACEHOLDER,
  PRODUCTION_STEP_GROUP_KEY,
  TASKS_GRID_STEP_ACTIONS_ARIA_LABEL,
  TASKS_SHEET_EMPTY_VALUE,
  TASKS_SHEET_PRODUCTION_EXPECTED_ONLY,
  TASKS_SHEET_PRODUCTION_SHOW_RANGE,
  TASKS_SHEET_REVENUE_LABEL,
  TASKS_SHEET_YIELD_UNIT,
} from 'constants/uiConstants';
import { IInputStep, IInputStepProduction } from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import { formatCurrency } from 'utils/CurrencyUtils';
import MergeClassNames from 'utils/MergeClassNames';

import {
  focusAdjacentStepCell,
  productionSheetNavColumn,
  STEP_NAV_COL_ATTR,
  STEP_NAV_COL_COST,
  STEP_NAV_COL_RATE,
} from './focusAdjacentStepCell';
import {
  hasProductionRange,
  isProductionRangeKey,
} from './productionScenarios';
import { ProductionMatrixKey, StepSelection } from './stepFactory';
import StepInputSelector from './StepInputSelector';
import { isFocusLeftWithPicker } from './taskEntryFocus';
import { RowKebabButton } from './TaskRowContextMenu';
import type { SheetColumnVisibility } from './tasksViewPreferences';
import type { useStepContextMenu } from './useStepContextMenu';

type StepContextMenuApi = ReturnType<typeof useStepContextMenu>;

interface SheetValueColumn {
  key: ProductionMatrixKey;
  isRequired: boolean;
  isPrice: boolean;
}

// Low, Expected, High yield, then Low, Expected, High price — the production
// matrix (OP-2128) laid out along one row. Expected is the required pair.
export const PRODUCTION_SHEET_COLUMNS: SheetValueColumn[] = [
  { key: 'yieldLow', isRequired: false, isPrice: false },
  { key: 'yield', isRequired: true, isPrice: false },
  { key: 'yieldHigh', isRequired: false, isPrice: false },
  { key: 'priceLow', isRequired: false, isPrice: true },
  { key: 'price', isRequired: true, isPrice: true },
  { key: 'priceHigh', isRequired: false, isPrice: true },
];

interface ProductionSheetRowProps {
  task: IInputTask;
  step: IInputStepProduction;
  leadingCell: React.ReactNode;
  /**
   * Set in the All view: the task sits in the sheet's product columns —
   * Expected yield as the Rate (t/ha), Expected price as the Cost /unit and its
   * revenue under $/ha. Unset under the Production filter, which shows all
   * three scenarios along the row.
   */
  inlineColumns?: SheetColumnVisibility;
  /**
   * All view: switches the grid to the Production filter, where Low, Expected
   * and High show, from the note under the crop that only Expected does here.
   */
  onShowAllScenarios?: () => void;
  /** Rendered after the value cells: the sheet's Notes column. */
  trailingCell?: React.ReactNode;
  editingStepInputId: string | null;
  setEditingStepInputId: (stepId: string | null) => void;
  handleStepInputChange: (
    task: IInputTask,
    stepId: string,
    selections: StepSelection[]
  ) => void;
  commitStepField: (
    task: IInputTask,
    stepId: string,
    patch: Partial<IInputStep>
  ) => void;
  stepContextMenu: StepContextMenuApi;
}

// A Production task's row in the category sheet (OP-2556): the Task cell,
// its crop (click to change), and its yield and price scenarios. Arrow
// Up/Down moves to the same scenario of the next production task. In the All
// view (`inlineColumns`) only Expected shows, in the product columns.
const ProductionSheetRow: React.FC<ProductionSheetRowProps> = ({
  task,
  step,
  leadingCell,
  inlineColumns,
  onShowAllScenarios,
  trailingCell,
  editingStepInputId,
  setEditingStepInputId,
  handleStepInputChange,
  commitStepField,
  stepContextMenu,
}) => {
  const { contextMenuState } = stepContextMenu;
  // As on the product rows: the crop cell takes Tab focus, Enter or Space
  // opens its picker, and focus comes back to it when the picker closes.
  const isPickerOpen = editingStepInputId === step.stepId;
  const cropTriggerRef = useRef<HTMLDivElement>(null);
  const [isHoverSuppressed, setIsHoverSuppressed] = useState(false);
  const wasPickerOpenRef = useRef(isPickerOpen);
  useEffect(() => {
    if (wasPickerOpenRef.current && !isPickerOpen) {
      // The pointer usually still rests on the cell, so its hover outline
      // would stay lit after the user Tabs on — reading as a stuck focus.
      // It stays off until the pointer moves again.
      setIsHoverSuppressed(true);
      const frame = requestAnimationFrame(() => {
        if (isFocusLeftWithPicker()) cropTriggerRef.current?.focus();
      });
      wasPickerOpenRef.current = isPickerOpen;
      return () => cancelAnimationFrame(frame);
    }
    wasPickerOpenRef.current = isPickerOpen;
  }, [isPickerOpen]);

  const hasRange = hasProductionRange(task);
  const isHighlighted =
    contextMenuState?.target.rowType === 'step' &&
    contextMenuState.target.taskId === task.taskId &&
    contextMenuState.target.stepId === step.stepId;

  const renderValueField = (
    key: ProductionMatrixKey,
    navColumn: string,
    isPrice: boolean
  ) => {
    const value = step[key] ?? null;
    return (
      <td
        className="relative border border-gray-medium p-0"
        {...{ [STEP_NAV_COL_ATTR]: navColumn }}
      >
        <div className="absolute inset-0">
          <NumericInputField
            className="h-full w-full"
            inputClassName="h-full w-full"
            min={0}
            inclusiveMin
            value={value}
            displayValue={
              value === null ? (
                <span className="text-gray-muted">
                  {PRODUCTION_MATRIX_REQUIRED_PLACEHOLDER}
                </span>
              ) : isPrice ? (
                formatCurrency(value)
              ) : (
                value
              )
            }
            onCommit={(next) =>
              commitStepField(task, step.stepId, { [key]: next })
            }
            onArrowNavigate={focusAdjacentStepCell}
          />
        </div>
      </td>
    );
  };

  const greyCell = (content: React.ReactNode, alignRight = false) => (
    <td className="border border-gray-medium bg-rowHover p-0 text-gray-dark">
      <div
        className={MergeClassNames(
          'flex h-full w-full items-center px-2 py-1 text-sm font-semibold',
          alignRight && 'justify-end'
        )}
      >
        {content}
      </div>
    </td>
  );

  // Revenue, not a cost: "+" and the word (on hover and for screen readers),
  // not the green alone.
  const revenue =
    step.yield !== null && step.price !== null ? step.yield * step.price : null;

  const renderInlineCells = (columns: SheetColumnVisibility) => (
    <>
      {renderValueField('yield', STEP_NAV_COL_RATE, false)}
      {columns.rateUnit &&
        greyCell(step.yieldUnit?.unit || TASKS_SHEET_YIELD_UNIT)}
      {columns.required &&
        greyCell(
          <span className="text-gray-muted">{TASKS_SHEET_EMPTY_VALUE}</span>
        )}
      {columns.costPerUnit &&
        renderValueField('price', STEP_NAV_COL_COST, true)}
      {columns.costPerHa &&
        greyCell(
          revenue === null ? (
            ' '
          ) : (
            <Tooltip title={TASKS_SHEET_REVENUE_LABEL}>
              <span className="text-success">
                <span className="sr-only">{TASKS_SHEET_REVENUE_LABEL} </span>+
                {formatCurrency(revenue)}
              </span>
            </Tooltip>
          ),
          true
        )}
    </>
  );

  return (
    <tr
      className={MergeClassNames(
        'group bg-white',
        isHighlighted && 'highlighted-row'
      )}
      onClick={(event) => {
        event.stopPropagation();
        stepContextMenu.closeContextMenu();
      }}
      onContextMenu={(event) =>
        stepContextMenu.handleStepRowContextMenu(
          event,
          task.taskId,
          step.stepId
        )
      }
    >
      {leadingCell}
      <td className="group/product relative border border-gray-medium p-0">
        {/* The hover and focus outline goes round the whole cell, the
            "Expected only" note included, not just the product's name. */}
        <div
          aria-hidden
          className={MergeClassNames(
            'pointer-events-none absolute inset-0 border border-transparent group-focus-within/product:border-success-dark',
            !isHoverSuppressed && 'group-hover/product:border-success'
          )}
        />
        <Popover
          trigger="click"
          placement="right"
          autoAdjustOverflow
          destroyOnHidden
          getPopupContainer={() => document.body}
          open={isPickerOpen}
          onOpenChange={(open) => {
            stepContextMenu.closeContextMenu();
            setEditingStepInputId(open ? step.stepId : null);
          }}
          classNames={{ root: 'step-input-selector-popover' }}
          content={
            <StepInputSelector
              taskType={task.taskType}
              initialCategoryKey={PRODUCTION_STEP_GROUP_KEY}
              // Changes the product; costs are added separately (OP-2399).
              categoryKeys={[PRODUCTION_STEP_GROUP_KEY]}
              initialSearchText={step.item}
              onClose={() => setEditingStepInputId(null)}
              onSelect={(selections) =>
                handleStepInputChange(task, step.stepId, selections)
              }
              task={task}
            />
          }
        >
          <div
            ref={cropTriggerRef}
            onPointerMove={() => setIsHoverSuppressed(false)}
            role="button"
            tabIndex={0}
            aria-haspopup="dialog"
            aria-expanded={isPickerOpen}
            className="flex h-full w-full cursor-pointer items-center border border-transparent py-1 pl-2 pr-7 outline-none"
            onKeyDown={(event) => {
              if (
                (event.key === 'Enter' || event.key === ' ') &&
                !isPickerOpen
              ) {
                event.preventDefault();
                stepContextMenu.closeContextMenu();
                setEditingStepInputId(step.stepId);
              }
            }}
          >
            <div className="block w-full min-w-0 flex-1 truncate">
              {step.item}
            </div>
            <IoMdArrowDropdown className="ml-1 flex-shrink-0 text-gray-dark" />
          </div>
        </Popover>
        {inlineColumns && hasRange && (
          // Only the Expected yield and price show in All; the full
          // Low–High range is under the Production filter.
          <div className="flex flex-wrap items-center gap-x-1 px-2 pb-1 text-xs text-gray-dark">
            {TASKS_SHEET_PRODUCTION_EXPECTED_ONLY}
            {onShowAllScenarios && (
              <LinkButton className="text-xs" onClick={onShowAllScenarios}>
                {TASKS_SHEET_PRODUCTION_SHOW_RANGE}
              </LinkButton>
            )}
          </div>
        )}
        {/* Inside the cell, so the grid can run edge to edge. */}
        <div className="absolute inset-y-0 right-0 z-10 flex w-6 items-center justify-center">
          <RowKebabButton
            label={TASKS_GRID_STEP_ACTIONS_ARIA_LABEL}
            isActive={isHighlighted}
            onOpen={(event) =>
              stepContextMenu.openMenuFromKebab(event, {
                rowType: 'step',
                taskId: task.taskId,
                stepId: step.stepId,
              })
            }
          />
        </div>
      </td>
      {inlineColumns
        ? renderInlineCells(inlineColumns)
        : PRODUCTION_SHEET_COLUMNS.map((column) => {
            // An Actual task has no Low or High: its cell stays, so the
            // columns line up, but is empty and can't be edited.
            if (!hasRange && isProductionRangeKey(column.key)) {
              return (
                <td
                  key={column.key}
                  className="border border-gray-medium bg-secondary-light"
                />
              );
            }
            const value = step[column.key] ?? null;
            const emptyDisplay = column.isRequired ? (
              <span className="text-gray-muted">
                {PRODUCTION_MATRIX_REQUIRED_PLACEHOLDER}
              </span>
            ) : (
              ' '
            );
            return (
              <td
                key={column.key}
                className="relative border border-gray-medium p-0"
                {...{
                  [STEP_NAV_COL_ATTR]: productionSheetNavColumn(column.key),
                }}
              >
                <div className="absolute inset-0">
                  <NumericInputField
                    className="h-full w-full"
                    inputClassName="h-full w-full"
                    min={0}
                    inclusiveMin
                    allowEmpty={!column.isRequired}
                    value={value}
                    displayValue={
                      value === null
                        ? emptyDisplay
                        : column.isPrice
                          ? formatCurrency(value)
                          : value
                    }
                    onCommit={(next) =>
                      commitStepField(task, step.stepId, { [column.key]: next })
                    }
                    onArrowNavigate={focusAdjacentStepCell}
                  />
                </div>
              </td>
            );
          })}
      {trailingCell}
    </tr>
  );
};

export default ProductionSheetRow;
